import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { ethers } from 'ethers';
import { useWizard } from '../../contexts/WizardContext';
import { useWallet } from '../../contexts/WalletContext';
import { isStellarNetwork } from '../../config/networks';
import {
  fetchStellarRegistryOracleKeys,
  readStellarValidationConfig,
  submitStellarValidationConfiguration,
} from '../../services/stellar';
import './Step5_1OnChainConfig.css';

const VALIDATION_ENGINE_ABI = [
  'function trustlineOracleEnabled() view returns (bool)',
  'function sanctionsOracleEnabled() view returns (bool)',
  'function sanctionsOracle() view returns (address)',
  'function validationRegistry() view returns (address)',
  'function setValidationConfiguration(bool trustlineEnabled, bool sanctionsEnabled, string memory sanctionsOracleKey) external',
] as const;

const VALIDATOR_REGISTRY_ABI = [
  'function getAllRecords() view returns (string[] keys, address[] addrs)',
] as const;

const SANCTIONS_ONLY_VALIDATION_ENGINE_ABI = [
  'function setValidationConfiguration(bool sanctionsEnabled, address sanctionsOracle_) external',
] as const;

const ORACLE_KEY_SUFFIX = '_ORACLE';

const Step5_1OnChainConfig: React.FC = () => {
  const { wizardData, updateWizardData } = useWizard();
  const {
    signer,
    isConnected,
    currentProvider,
    account,
    walletKind,
    connectFreighter,
    signStellarTransaction,
  } = useWallet();

  const [onChainTrustline, setOnChainTrustline] = useState<boolean | null>(null);
  const [onChainSanctions, setOnChainSanctions] = useState<boolean | null>(null);
  const [onChainSanctionsOracleAddress, setOnChainSanctionsOracleAddress] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [sanctionsOracleKeys, setSanctionsOracleKeys] = useState<string[]>([]);
  const [sanctionsOracleKeyToAddress, setSanctionsOracleKeyToAddress] = useState<Record<string, string>>({});
  const [oracleKeysFetchError, setOracleKeysFetchError] = useState<string | null>(null);
  const [isFetchingOracleKeys, setIsFetchingOracleKeys] = useState(false);
  const [isSubmittingTx, setIsSubmittingTx] = useState(false);
  const [txError, setTxError] = useState<string | null>(null);

  const stellar = isStellarNetwork(wizardData.chain);
  const desiredTrustline = useMemo(() => {
    return wizardData.methodMappings.some(m => m.policyId !== null);
  }, [wizardData.methodMappings]);

  const desiredSanctions = wizardData.sanctionsEnabled;

  const proxyAddress = wizardData.validationEngineProxyAddress;
  const chain = wizardData.chain;

  const fetchOnChainState = useCallback(async (silent = false) => {
    if (!proxyAddress || !chain?.rpcUrl) return;
    if (!silent) {
      setIsFetching(true);
      setFetchError(null);
    }
    try {
      if (isStellarNetwork(chain)) {
        const state = await readStellarValidationConfig(chain, proxyAddress);
        setOnChainTrustline(state.trustlineEnabled);
        setOnChainSanctions(state.sanctionsEnabled);
        setOnChainSanctionsOracleAddress(null);
        return;
      }

      const provider = new ethers.JsonRpcProvider(chain.rpcUrl);
      const contract = new ethers.Contract(proxyAddress, VALIDATION_ENGINE_ABI, provider);
      const [trustline, sanctions, sanctionsOracleAddr] = await Promise.all([
        contract.trustlineOracleEnabled(),
        contract.sanctionsOracleEnabled(),
        contract.sanctionsOracle(),
      ]);
      setOnChainTrustline(Boolean(trustline));
      setOnChainSanctions(Boolean(sanctions));
      setOnChainSanctionsOracleAddress(sanctionsOracleAddr && sanctionsOracleAddr !== ethers.ZeroAddress ? ethers.getAddress(sanctionsOracleAddr) : null);
    } catch (err: any) {
      if (!silent) {
        console.error('Error fetching on-chain config:', err);
        setFetchError(err.message || 'Failed to read on-chain configuration');
        setOnChainTrustline(null);
        setOnChainSanctions(null);
        setOnChainSanctionsOracleAddress(null);
      }
    } finally {
      if (!silent) {
        setIsFetching(false);
      }
    }
  }, [proxyAddress, chain]);

  const fetchOracleKeys = useCallback(async () => {
    if (!proxyAddress || !chain?.rpcUrl) return;
    setIsFetchingOracleKeys(true);
    setOracleKeysFetchError(null);
    try {
      if (isStellarNetwork(chain)) {
        const { keys, keyToAddress } = await fetchStellarRegistryOracleKeys(chain, proxyAddress);
        setSanctionsOracleKeys(keys);
        setSanctionsOracleKeyToAddress(keyToAddress);
        return;
      }

      const provider = new ethers.JsonRpcProvider(chain.rpcUrl);
      const engineContract = new ethers.Contract(proxyAddress, VALIDATION_ENGINE_ABI, provider);
      const registryAddress = await engineContract.validationRegistry();
      if (!registryAddress || registryAddress === ethers.ZeroAddress) {
        setOracleKeysFetchError('No validation registry on this engine');
        setSanctionsOracleKeys([]);
        return;
      }
      const registryContract = new ethers.Contract(registryAddress, VALIDATOR_REGISTRY_ABI, provider);
      const [keys, addrs] = await registryContract.getAllRecords();
      const keyList = keys as string[];
      const addrList = addrs as string[];
      const oracleKeys: string[] = [];
      const keyToAddr: Record<string, string> = {};
      keyList.forEach((k: string, i: number) => {
        if (typeof k === 'string' && k.endsWith(ORACLE_KEY_SUFFIX) && addrList[i]) {
          oracleKeys.push(k);
          keyToAddr[k] = ethers.getAddress(addrList[i]);
        }
      });
      setSanctionsOracleKeys(oracleKeys);
      setSanctionsOracleKeyToAddress(keyToAddr);
    } catch (err: any) {
      console.error('Error fetching oracle keys:', err);
      setOracleKeysFetchError(err.message || 'Failed to load sanctions oracle keys');
      setSanctionsOracleKeys([]);
      setSanctionsOracleKeyToAddress({});
    } finally {
      setIsFetchingOracleKeys(false);
    }
  }, [proxyAddress, chain]);

  useEffect(() => {
    fetchOnChainState(false);
  }, [fetchOnChainState]);

  useEffect(() => {
    if (proxyAddress && chain?.rpcUrl) {
      fetchOracleKeys();
    }
  }, [proxyAddress, chain?.rpcUrl, fetchOracleKeys]);

  const noOraclesAvailable = !isFetchingOracleKeys && !oracleKeysFetchError && sanctionsOracleKeys.length === 0;

  useEffect(() => {
    if (noOraclesAvailable) {
      updateWizardData({ sanctionsEnabled: false, sanctionsOracleKey: null });
    }
  }, [noOraclesAvailable, updateWizardData]);

  const configMatches = useMemo(() => {
    if (onChainTrustline === null || onChainSanctions === null || desiredSanctions === null) return false;
    if (onChainTrustline !== desiredTrustline || onChainSanctions !== desiredSanctions) return false;
    if (!desiredSanctions) return true;
    // Stellar stores the sanctions key on-chain but does not expose a getter for it;
    // once flags match and a key is selected, treat as synced after a successful write
    // or when sanctions were already enabled on-chain.
    if (stellar) {
      return !!wizardData.sanctionsOracleKey?.trim() || onChainSanctions === true;
    }
    const selectedKey = wizardData.sanctionsOracleKey?.trim();
    if (!selectedKey) return false;
    const expectedAddr = sanctionsOracleKeyToAddress[selectedKey];
    if (!expectedAddr || !onChainSanctionsOracleAddress) return false;
    try {
      return ethers.getAddress(expectedAddr) === ethers.getAddress(onChainSanctionsOracleAddress);
    } catch {
      return expectedAddr.toLowerCase() === onChainSanctionsOracleAddress.toLowerCase();
    }
  }, [
    onChainTrustline,
    onChainSanctions,
    onChainSanctionsOracleAddress,
    desiredTrustline,
    desiredSanctions,
    wizardData.sanctionsOracleKey,
    sanctionsOracleKeyToAddress,
    stellar,
  ]);

  useEffect(() => {
    updateWizardData({ onChainConfigSynced: configMatches });
  }, [configMatches, updateWizardData]);

  useEffect(() => {
    if (!proxyAddress || !chain?.rpcUrl) return;
    const interval = setInterval(() => {
      fetchOnChainState(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [proxyAddress, chain?.rpcUrl, fetchOnChainState]);

  const handleSanctionsChange = (enabled: boolean) => {
    updateWizardData({
      sanctionsEnabled: enabled,
      sanctionsOracleKey: enabled ? wizardData.sanctionsOracleKey : null,
      onChainConfigSynced: false,
    });
    setTxError(null);
  };

  const handleOracleKeyChange = (key: string) => {
    updateWizardData({ sanctionsOracleKey: key || null, onChainConfigSynced: false });
    setTxError(null);
  };

  const sanctionsKeyForTx = (desiredSanctions && wizardData.sanctionsOracleKey?.trim()) ? wizardData.sanctionsOracleKey.trim() : '';
  const sanctionsOracleAddressForTx = sanctionsKeyForTx ? sanctionsOracleKeyToAddress[sanctionsKeyForTx] : '';

  const ensureWalletOnSelectedNetwork = useCallback(async (): Promise<void> => {
    if (!currentProvider || !chain) {
      throw new Error('No wallet provider or target chain available');
    }

    const selectedChainHex = `0x${chain.chainId.toString(16)}`;
    const currentChainHex = await currentProvider.request({ method: 'eth_chainId' });

    if ((currentChainHex || '').toLowerCase() === selectedChainHex.toLowerCase()) {
      return;
    }

    try {
      await currentProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: selectedChainHex }],
      });
    } catch (switchErr: any) {
      if (switchErr?.code === 4902) {
        await currentProvider.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: selectedChainHex,
            chainName: chain.name,
            rpcUrls: [chain.rpcUrl],
            blockExplorerUrls: [chain.blockExplorer],
            nativeCurrency: chain.nativeCurrency,
          }],
        });
      } else {
        throw switchErr;
      }
    }
  }, [currentProvider, chain]);

  const handleSubmitTransaction = async () => {
    if (!proxyAddress || desiredSanctions === null || !chain) return;
    if (desiredSanctions && !sanctionsKeyForTx) return;
    if (desiredSanctions && !stellar && !sanctionsOracleAddressForTx) return;

    setIsSubmittingTx(true);
    setTxError(null);

    try {
      if (stellar) {
        if (walletKind !== 'stellar' || !account) {
          throw new Error('Connect Freighter to submit the Stellar configuration transaction');
        }
        await submitStellarValidationConfiguration({
          chain,
          validationEngineAddress: proxyAddress,
          publicKey: account,
          signTransaction: signStellarTransaction,
          trustlineEnabled: desiredTrustline,
          sanctionsEnabled: desiredSanctions,
          sanctionsKey: sanctionsKeyForTx || null,
        });
        await fetchOnChainState(false);
        updateWizardData({ onChainConfigSynced: true });
        return;
      }

      if (!signer) return;
      const fallbackOracleAddress = desiredSanctions ? sanctionsOracleAddressForTx : ethers.ZeroAddress;
      await ensureWalletOnSelectedNetwork();
      const runtimeSigner = currentProvider
        ? await new ethers.BrowserProvider(currentProvider).getSigner()
        : signer;

      const contract = new ethers.Contract(proxyAddress, VALIDATION_ENGINE_ABI, runtimeSigner);
      const tx = await contract.setValidationConfiguration(desiredTrustline, desiredSanctions, sanctionsKeyForTx);
      await tx.wait();
      await fetchOnChainState(false);
    } catch (primaryErr: any) {
      if (stellar) {
        console.error('Stellar set_validation_configuration failed:', primaryErr);
        setTxError(primaryErr?.message || 'Configuration transaction failed');
        return;
      }

      console.warn('Primary setValidationConfiguration call failed, trying sanctions-only fallback:', primaryErr);
      try {
        await ensureWalletOnSelectedNetwork();
        const runtimeSigner = currentProvider
          ? await new ethers.BrowserProvider(currentProvider).getSigner()
          : signer;

        const fallbackOracleAddress = desiredSanctions ? sanctionsOracleAddressForTx : ethers.ZeroAddress;
        const fallbackContract = new ethers.Contract(proxyAddress, SANCTIONS_ONLY_VALIDATION_ENGINE_ABI, runtimeSigner);
        const fallbackTx = await fallbackContract.setValidationConfiguration(desiredSanctions, fallbackOracleAddress);
        await fallbackTx.wait();
        await fetchOnChainState(false);
      } catch (fallbackErr: any) {
        console.error('Both primary and fallback transactions failed:', primaryErr, fallbackErr);
        const primaryMessage = primaryErr?.reason || primaryErr?.message || 'Primary method failed';
        const fallbackMessage = fallbackErr?.reason || fallbackErr?.message || 'Fallback method failed';
        setTxError(`Configuration transaction failed. Primary: ${primaryMessage}. Fallback: ${fallbackMessage}`);

        const networkIssue =
          primaryErr?.code === 4902 || fallbackErr?.code === 4902 ||
          primaryErr?.code === 4001 || fallbackErr?.code === 4001 ||
          primaryErr?.code === 'NETWORK_ERROR' || fallbackErr?.code === 'NETWORK_ERROR' ||
          (primaryErr?.message && primaryErr.message.toLowerCase().includes('network')) ||
          (fallbackErr?.message && fallbackErr.message.toLowerCase().includes('network'));
        if (networkIssue) {
          setTxError('Wrong network. Please switch your wallet to ' + (chain?.name || 'the correct chain') + '.');
        }
      }
    } finally {
      setIsSubmittingTx(false);
    }
  };

  const canSubmitTx = stellar
    ? isConnected && walletKind === 'stellar' && !!account && !!proxyAddress && !!chain &&
      (desiredSanctions === false || (desiredSanctions === true && sanctionsKeyForTx !== ''))
    : isConnected && !!signer && !!proxyAddress && !!chain &&
      (desiredSanctions === false || (desiredSanctions === true && sanctionsKeyForTx !== '' && !!sanctionsOracleAddressForTx));

  return (
    <div className="wizard-step step5-1-onchain-config">
      <h2 className="wizard-step-title">On-Chain Behaviour Configuration</h2>
      <p className="wizard-step-description">
        Trustline includes on-chain settings that live on your Validation Engine{stellar ? '' : ' Proxy'}. These options control how validation behaves when your contract is called. Applying this configuration requires you to sign a transaction, since it updates state on your {stellar ? 'Validation Engine' : 'proxy'} contract.
      </p>

      <div className="step5-1-config-block">
        <h3 className="step5-1-config-title">Configuration options</h3>

        <div className="step5-1-option step5-1-option-readonly">
          <div className="step5-1-option-header">
            <span className="step5-1-option-label">Trustline Oracle validation</span>
            <span className={`step5-1-option-badge ${desiredTrustline ? 'step5-1-badge-on' : 'step5-1-badge-off'}`}>
              {desiredTrustline ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <p className="step5-1-option-desc">
            Enabled when at least one contract method has a policy mapped. This turns on Trustline validation for all methods that use{' '}
            <code>{stellar ? 'require_trustline()' : 'requireTrustline()'}</code>.
          </p>
        </div>

        <div className="step5-1-option">
          <div className="step5-1-option-header">
            <span className="step5-1-option-label">OFAC sanctions check</span>
            {noOraclesAvailable && (
              <span className="step5-1-option-badge step5-1-badge-off">Disabled</span>
            )}
          </div>
          <p className="step5-1-option-desc">
            When enabled, the caller and any addresses passed to{' '}
            <code>{stellar ? 'require_trustline_addrs()' : 'requireTrustline()'}</code>{' '}
            are checked against the OFAC sanctions list when available.
          </p>
          {noOraclesAvailable ? (
            <p className="step5-1-option-desc step5-1-forced-disabled-text">
              This chain does not support sanctions oracles. No oracle keys are available in the registry, so the sanctions check is set to disabled.
            </p>
          ) : (
            <>
              <div className="step5-1-radio-group">
                <label className="step5-1-radio-label">
                  <input
                    type="radio"
                    name="sanctions"
                    checked={wizardData.sanctionsEnabled === true}
                    onChange={() => handleSanctionsChange(true)}
                  />
                  <span className="step5-1-radio-text">Enable</span>
                </label>
                <label className="step5-1-radio-label">
                  <input
                    type="radio"
                    name="sanctions"
                    checked={wizardData.sanctionsEnabled === false}
                    onChange={() => handleSanctionsChange(false)}
                  />
                  <span className="step5-1-radio-text">Disable</span>
                </label>
              </div>
              {wizardData.sanctionsEnabled === true && (
            <div className="step5-1-oracle-key-group">
              <label htmlFor="sanctions-oracle-key" className="step5-1-option-label">Sanctions oracle</label>
              {isFetchingOracleKeys ? (
                <p className="step5-1-option-desc">Loading oracle keys from registry…</p>
              ) : oracleKeysFetchError ? (
                <p className="wizard-error">{oracleKeysFetchError}</p>
              ) : sanctionsOracleKeys.length === 0 ? (
                <p className="step5-1-option-desc">No oracle keys found in the registry.</p>
              ) : (
                <select
                  id="sanctions-oracle-key"
                  value={wizardData.sanctionsOracleKey ?? ''}
                  onChange={(e) => handleOracleKeyChange(e.target.value)}
                  className="step5-1-oracle-key-select"
                >
                  <option value="">Select a sanctions oracle…</option>
                  {sanctionsOracleKeys.map((key) => (
                    <option key={key} value={key}>{key}</option>
                  ))}
                </select>
              )}
            </div>
              )}
              </>
            )}
        </div>

        {chain && proxyAddress && (
          <div className="step5-1-config-status">
            {isFetching ? (
              <p className="step5-1-onchain-status">Reading on-chain configuration…</p>
            ) : fetchError ? (
              <div className="step5-1-onchain-error">
                <p>{fetchError}</p>
                <button type="button" onClick={() => fetchOnChainState(false)} className="step5-1-retry-btn">
                  Retry
                </button>
              </div>
            ) : onChainTrustline !== null && onChainSanctions !== null ? (
              wizardData.sanctionsEnabled === null ? (
                <p className="step5-1-onchain-hint">Choose an option above to see if a transaction is required.</p>
              ) : wizardData.sanctionsEnabled === true && !sanctionsKeyForTx && !noOraclesAvailable ? (
                <p className="step5-1-onchain-hint">Select a sanctions oracle above to continue.</p>
              ) : configMatches ? (
                <div className="step5-1-success-block">
                  <div className="step5-1-success-icon">✓</div>
                  <p className="step5-1-success-message">Configuration is up to date. You can proceed to the next step.</p>
                </div>
              ) : (
                <>
                  <p className="step5-1-option-desc">
                    Click the button below to send your configuration on-chain.
                  </p>
                  {stellar && walletKind !== 'stellar' && (
                    <div className="step5-1-tx-warning">
                      <p>Connect Freighter to submit the Stellar configuration transaction.</p>
                      <button type="button" onClick={() => connectFreighter()} className="step5-1-retry-btn">
                        Connect Freighter
                      </button>
                    </div>
                  )}
                  {!canSubmitTx && !(stellar && walletKind !== 'stellar') && (
                    <p className="step5-1-tx-warning">Connect your wallet (and ensure it is on {chain.name}) to submit the transaction.</p>
                  )}
                  {txError && <div className="wizard-error step5-1-tx-error">{txError}</div>}
                  <button
                    type="button"
                    onClick={handleSubmitTransaction}
                    disabled={!canSubmitTx || isSubmittingTx}
                    className="step5-1-submit-tx-btn"
                  >
                    {isSubmittingTx ? 'Submitting…' : 'Submit transaction'}
                  </button>
                </>
              )
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default Step5_1OnChainConfig;
